import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const BadgeContext = createContext(null);

export function BadgeProvider({ children }) {
  const [badgeMap, setBadgeMap] = useState({});

  // 设置某个菜单项徽章
  const setBadge = useCallback((key, value) => {
    setBadgeMap(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // 清除某个菜单项徽章
  const clearBadge = useCallback((key) => {
    setBadgeMap(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // 一次性更新多个徽章
  const setBadges = useCallback((badges) => {
    setBadgeMap(prev => ({
      ...prev,
      ...badges,
    }));
  }, []);

  // 增加数字徽章
  const incrementBadge = useCallback((key, step = 1) => {
    setBadgeMap(prev => {
      const current = prev[key];
      const currentNumber =
        typeof current === 'number'
          ? current
          : parseInt(current, 10) || 0;

      return {
        ...prev,
        [key]: currentNumber + step,
      };
    });
  }, []);

  // 减少数字徽章
  const decrementBadge = useCallback((key, step = 1) => {
    setBadgeMap(prev => {
      const current = prev[key];
      const currentNumber =
        typeof current === 'number'
          ? current
          : parseInt(current, 10) || 0;

      const nextValue = Math.max(0, currentNumber - step);

      return {
        ...prev,
        [key]: nextValue > 0 ? nextValue : null,
      };
    });
  }, []);

  const getBadge = useCallback((key) => {
    return badgeMap[key];
  }, [badgeMap]);

  const value = useMemo(() => ({
    badgeMap,
    setBadge,
    clearBadge,
    setBadges,
    incrementBadge,
    decrementBadge,
    getBadge,
  }), [
    badgeMap,
    setBadge,
    clearBadge,
    setBadges,
    incrementBadge,
    decrementBadge,
    getBadge,
  ]);

  return (
    <BadgeContext.Provider value={value}>
      {children}
    </BadgeContext.Provider>
  );
}

export function useBadge() {
  const context = useContext(BadgeContext);
  if (!context) {
    throw new Error('useBadge must be used within BadgeProvider');
  }
  return context;
}